/**
 * useApiClient — binds the ambient auth context to the ApiClient surface.
 * The access token is read at CALL time (memory-only, §11.1), so a client
 * captured before a token refresh still sends the fresh token.
 */

import { useMemo } from "react";
import type { ApiClient } from "../api/client";
import { apiRequest } from "../api/http";
import { useAuth } from "../auth/AuthProvider";

export function useApiClient(): ApiClient {
  const { transport, getAccessToken } = useAuth();
  return useMemo<ApiClient>(
    () => ({
      request<T>(call: {
        method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
        path: string;
        body?: unknown;
        headers?: Readonly<Record<string, string>>;
      }) {
        const accessToken = getAccessToken();
        return apiRequest<T>(transport, {
          method: call.method,
          path: call.path,
          ...(call.body !== undefined ? { body: call.body } : {}),
          ...(call.headers !== undefined ? { headers: call.headers } : {}),
          ...(accessToken !== null ? { accessToken } : {}),
        });
      },
    }),
    [transport, getAccessToken],
  );
}
